// Admin-only endpoints. Behind `require_admin!` server-side
// (env-allowlist via ABG_ADMIN_USER_IDS). Non-admins receive 403.
// Phase 7+ admin UI consumes these.

import type {
  KolActivity,
  KolActivityKind,
  KolPayout,
  KolPayoutMethod,
  KolReferralCode,
  KolStats,
  KolStatus,
  KolWithStats,
  Tier,
} from "@/types/api";
import type { QuizBoard, QuizCategory } from "./quiz";
import { request } from "./transport";

export interface AdminUserSummary {
  readonly id: number;
  readonly name: string;
  readonly email: string | null;
  readonly pin: string;
  readonly tier: Tier;
  readonly active: boolean;
  readonly admin: boolean;
  readonly expires_at: string | null;
  readonly paid_at: string | null;
  readonly created_at: string;
  readonly total_spent: number;
  readonly payment_count: number;
}

export interface AdminUserPayment {
  readonly id: string;
  readonly amount_cents: number;
  readonly currency: string;
  readonly status: string;
  readonly created_at: string;
}

export interface AdminAccessGrant {
  readonly id: number;
  readonly payment_id: string | null;
  readonly kind: string;
  readonly days_granted: number;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly refunded_at: string | null;
  readonly revoked_days: number;
  readonly metadata: Record<string, unknown>;
  readonly created_at: string;
}

export interface AdminUserStats {
  readonly alerts: number;
  readonly backtest_runs: number;
  readonly transactions: number;
  readonly notes: number;
  readonly saved_strategies: number;
  readonly push_subscriptions: number;
}

export interface AdminUserDetail extends Omit<
  AdminUserSummary,
  "total_spent" | "payment_count"
> {
  readonly source?: {
    code: string;
    channel_name: string | null;
    kol_id: string | null;
    kol_name: string | null;
  } | null;
  readonly stats: AdminUserStats;
  readonly payments: readonly AdminUserPayment[];
  readonly access_grants: readonly AdminAccessGrant[];
  readonly total_spent: number;
  readonly payment_count: number;
}

export interface AdminUserRevenue {
  readonly paying_users: number;
  readonly total_cents: number;
}

export interface AdminUserMembershipCounts {
  readonly trial: number;
  readonly subscribed: number;
  readonly expired: number;
  readonly permanent: number;
}

export interface AdminUserFilter {
  readonly tier?: Tier;
  readonly q?: string;
  readonly limit?: number;
  readonly page?: number;
  readonly referral_code?: string;
  readonly paid_only?: boolean;
}

export interface AdminUserPatch {
  name?: string;
  email?: string | null;
  pin?: string;
  tier?: Tier;
  expires_at?: string | null;
  active?: boolean;
}

export const INVITE_DURATIONS = ["1w", "1m", "3m", "1y", "lifetime"] as const;
export type InviteDuration = (typeof INVITE_DURATIONS)[number];

export interface InviteUserInput {
  readonly email: string;
  readonly name: string;
  readonly duration: InviteDuration;
}

export interface AdminDashboardPayment {
  readonly id: string;
  readonly amount_cents: number;
  readonly email: string | null;
  readonly pin_generated: string | null;
  readonly created_at: string;
}

export interface AdminRevenuePoint {
  readonly month: string;
  readonly revenue: number;
  readonly cumulative: number;
}

export interface AdminSignupPoint {
  readonly month: string;
  readonly count: number;
}

export interface AdminDashboard {
  readonly total_revenue: number;
  readonly total_payments: number;
  readonly recent_payments: readonly AdminDashboardPayment[];
  readonly revenue_chart: readonly AdminRevenuePoint[];
  readonly signup_chart: readonly AdminSignupPoint[];
}

export interface AdminReferral {
  readonly id: string;
  readonly code: string;
  readonly owner_id: number;
  readonly referral_type: string;
  readonly reward_days_referrer: number;
  readonly uses: number;
  readonly max_uses: number | null;
  readonly active: boolean;
  readonly created_at: string;
}

export interface AdminReferralEvent {
  readonly id: string;
  readonly referral_id: string;
  readonly referred_user_id: number;
  readonly payment_id: string | null;
  readonly days_credited_referrer: number;
  readonly days_credited_invitee: number;
  readonly created_at: string;
}

export interface AdminReferralTotals {
  readonly events: number;
  readonly days_rewarded: number;
}

export interface AdminReferralBundle {
  readonly referrals: readonly AdminReferral[];
  readonly events: readonly AdminReferralEvent[];
  readonly totals: AdminReferralTotals;
}

export interface AdminNotificationStats {
  readonly recipients: number;
  readonly read: number;
  readonly archived: number;
}

export interface AdminNotification {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly url: string | null;
  readonly targeting: Record<string, unknown>;
  readonly created_at: string;
  readonly stats: AdminNotificationStats;
}

export type NotificationTargeting =
  | { type: "all" }
  | { type: "users"; ids: number[] }
  | { type: "tier"; tier: Tier }
  | { type: "expiry"; before?: string; after?: string };

export interface AdminNotificationInput {
  title: string;
  body: string;
  url?: string | null;
  targeting: NotificationTargeting;
}

export type AdminQuizStatus = "draft" | "active" | "archived";

export interface AdminQuizQuestion {
  readonly id: string;
  readonly key: string;
  readonly board: QuizBoard;
  readonly category: QuizCategory;
  readonly prompt_en: string;
  readonly prompt_zh: string;
  readonly choices_en: readonly string[];
  readonly choices_zh: readonly string[];
  readonly answer_index: number;
  readonly explanation_en: string;
  readonly explanation_zh: string;
  readonly status: AdminQuizStatus;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AdminQuizFilter {
  readonly board?: QuizBoard;
  readonly category?: QuizCategory;
  readonly status?: AdminQuizStatus;
  readonly q?: string;
}

export interface AdminQuizQuestionPatch {
  board?: QuizBoard;
  category?: QuizCategory;
  prompt_en?: string;
  prompt_zh?: string;
  choices_en?: readonly string[];
  choices_zh?: readonly string[];
  answer_index?: number;
  explanation_en?: string;
  explanation_zh?: string;
  status?: AdminQuizStatus;
}

export interface KolInput {
  name?: string;
  handle?: string | null;
  platform?: string;
  follower_count?: number;
  commission_bps?: number;
  status?: KolStatus;
  notes?: string | null;
  profile_url?: string | null;
  payout_method?: KolPayoutMethod;
  payout_contact?: string | null;
  email?: string | null;
  user_id?: number | null;
  slug?: string | null;
}

export interface KolPayoutInput {
  period_start: string;
  period_end: string;
  amount_cents: number;
  method?: KolPayoutMethod;
  reference?: string | null;
  note?: string | null;
  paid_at?: string;
}

export interface KolActivityInput {
  kind: KolActivityKind;
  body?: string | null;
  metadata?: Record<string, unknown>;
}

export interface KolCodeInput {
  code?: string;
  channel_name?: string;
  referral_type?: string;
  reward_days_referrer?: number;
  bonus_days_invitee?: number;
  max_uses?: number | null;
  active?: boolean;
}

export interface OnboardKolInput {
  email?: string;
  initial_code?: string;
  reward_days_referrer?: number;
  bonus_days_invitee?: number;
  max_uses?: number | null;
}

export interface OnboardKolResult {
  readonly user_id: number;
  readonly email: string;
  readonly pin: string | null;
  readonly code: string;
  readonly referral_id: string;
  readonly linked: boolean;
}

export const admin = {
  // Aggregate metrics for the AdminHealthTab.
  getAdminDashboard: () => request<AdminDashboard>("/admin/dashboard"),

  // Referral CRM.
  getAdminReferrals: () => request<AdminReferralBundle>("/admin/referral"),

  patchAdminReferral: (id: string, active: boolean) =>
    request<{ referral: AdminReferral }>("/admin/referral", {
      method: "PATCH",
      body: JSON.stringify({ id, active }),
    }),

  listKols: (params?: { status?: KolStatus; platform?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.platform) qs.set("platform", params.platform);
    const q = qs.toString();
    return request<{ kols: KolWithStats[] }>(`/admin/kols${q ? `?${q}` : ""}`);
  },

  getKol: (id: string) => request<{ kol: KolWithStats }>(`/admin/kols/${id}`),

  createKol: (input: KolInput) =>
    request<{ kol: KolWithStats }>("/admin/kols", {
      method: "POST",
      body: JSON.stringify({ kol: input }),
    }),

  updateKol: (id: string, patch: KolInput) =>
    request<{ kol: KolWithStats }>(`/admin/kols/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ kol: patch }),
    }),

  deleteKol: (id: string) =>
    request<{ ok: true }>(`/admin/kols/${id}`, { method: "DELETE" }),

  listKolPayouts: (kolId: string) =>
    request<{ payouts: KolPayout[] }>(`/admin/kols/${kolId}/payouts`),

  createKolPayout: (kolId: string, input: KolPayoutInput) =>
    request<{ payout: KolPayout; stats: KolStats }>(
      `/admin/kols/${kolId}/payouts`,
      {
        method: "POST",
        body: JSON.stringify({ payout: input }),
      },
    ),

  deleteKolPayout: (kolId: string, payoutId: string) =>
    request<{ ok: true }>(`/admin/kols/${kolId}/payouts/${payoutId}`, {
      method: "DELETE",
    }),

  listKolActivities: (kolId: string) =>
    request<{ activities: KolActivity[] }>(`/admin/kols/${kolId}/activities`),

  createKolActivity: (kolId: string, input: KolActivityInput) =>
    request<{ activity: KolActivity }>(`/admin/kols/${kolId}/activities`, {
      method: "POST",
      body: JSON.stringify({ activity: input }),
    }),

  listKolCodes: (kolId: string) =>
    request<{ codes: KolReferralCode[] }>(`/admin/kols/${kolId}/codes`),

  createKolCode: (kolId: string, input: KolCodeInput = {}) =>
    request<{ code: KolReferralCode }>(`/admin/kols/${kolId}/codes`, {
      method: "POST",
      body: JSON.stringify({ code: input }),
    }),

  renameKolCode: (kolId: string, codeId: string, channelName: string) =>
    request<{ code: { id: string; channel_name: string | null } }>(
      `/admin/kols/${kolId}/codes/${codeId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ code: { channel_name: channelName } }),
      },
    ),

  onboardKol: (kolId: string, input: OnboardKolInput) =>
    request<OnboardKolResult>(`/admin/kols/${kolId}/onboard`, {
      method: "POST",
      body: JSON.stringify({ onboard: input }),
    }),

  // Admin notification authoring — fans out NotificationTarget rows
  // server-side based on `targeting` spec. The frontend sends the spec,
  // not a resolved id list, so per-tier / per-locale targeting can land
  // server-only without a frontend change.
  listAdminNotifications: () =>
    request<{ notifications: AdminNotification[] }>("/admin/notifications"),

  createAdminNotification: (input: AdminNotificationInput) =>
    request<{ notification: AdminNotification }>("/admin/notifications", {
      method: "POST",
      body: JSON.stringify({ notification: input }),
    }),

  deleteAdminNotification: (id: string) =>
    request<{ ok: true }>(`/admin/notifications/${id}`, { method: "DELETE" }),

  listAdminQuizQuestions: (filter?: AdminQuizFilter) => {
    const qs = new URLSearchParams();
    if (filter?.board) qs.set("board", filter.board);
    if (filter?.category) qs.set("category", filter.category);
    if (filter?.status) qs.set("status", filter.status);
    if (filter?.q) qs.set("q", filter.q);
    const q = qs.toString();
    return request<{ questions: AdminQuizQuestion[] }>(
      `/admin/quiz_questions${q ? `?${q}` : ""}`,
    );
  },

  updateAdminQuizQuestion: (id: string, patch: AdminQuizQuestionPatch) =>
    request<{ question: AdminQuizQuestion }>(`/admin/quiz_questions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ question: patch }),
    }),

  archiveAdminQuizQuestion: (id: string) =>
    request<{ question: AdminQuizQuestion }>(`/admin/quiz_questions/${id}`, {
      method: "DELETE",
    }),

  // Admin users — full CRUD + resend-pin. The list endpoint joins
  // payments and returns total_spent + payment_count per user.
  listAdminUsers: (filter?: AdminUserFilter) => {
    const qs = new URLSearchParams();
    if (filter?.tier) qs.set("tier", filter.tier);
    if (filter?.q) qs.set("q", filter.q);
    if (filter?.limit) qs.set("limit", String(filter.limit));
    if (filter?.page) qs.set("page", String(filter.page));
    if (filter?.referral_code) qs.set("referral_code", filter.referral_code);
    if (filter?.paid_only) qs.set("paid_only", "true");
    const q = qs.toString();
    return request<{
      users: AdminUserSummary[];
      channel?: {
        code: string;
        channel_name: string | null;
        kol_id: string | null;
        kol_name: string | null;
      } | null;
      total: number;
      membership_counts: AdminUserMembershipCounts;
      revenue: AdminUserRevenue;
    }>(`/admin/users${q ? `?${q}` : ""}`);
  },

  getAdminUser: (id: number) =>
    request<{ user: AdminUserDetail }>(`/admin/users/${id}`),

  updateAdminUser: (id: number, patch: AdminUserPatch) =>
    request<{ user: AdminUserDetail }>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ user: patch }),
    }),

  deleteAdminUser: (id: number) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: "DELETE" }),

  resendAdminUserPin: (id: number) =>
    request<{ ok: true }>(`/admin/users/${id}/resend_pin`, { method: "POST" }),

  inviteAdminUser: (input: InviteUserInput) =>
    request<{ user: AdminUserSummary }>("/admin/users/invite", {
      method: "POST",
      body: JSON.stringify({ invite: input }),
    }),
};
